import * as React from "react";
import {observable, makeObservable, computed} from "mobx";
import type {Markup} from "../models/Markup";
import type {Boundary} from "../models/Boundary";
import type {SpaceVersion} from "../models/SpaceVersion";
import type {LibraryImage} from "../models/LibraryImage";
import type {Link} from "../models/Link";
import type {LibraryModel} from "../models/LibraryModel";
import {configurePhone} from "../models/field/datatypes/Phone";
import {configureIP} from "../models/field/datatypes/IP";
import {configureEmail} from "../models/field/datatypes/Email";
import {configureMultiSelect} from "../models/field/datatypes/MultiSelect";
import {configureSingleSelect} from "../models/field/datatypes/SingleSelect";
import {configureMultiLine} from "../models/field/datatypes/MultiLine";
import {configureSingleLine} from "../models/field/datatypes/SingleLine";
import {configureNumeric} from "../models/field/datatypes/Numeric";
import {configureDate} from "../models/field/datatypes/Date";
import {configureBoolean} from "../models/field/datatypes/Boolean";
import type {IModel} from "../models/Model";
import type {Space} from "../models/Space";
import type {ExternalUser, User} from "../models/User";
import type {Portfolio} from "../models/Portfolio";
import type {Xyicon} from "../models/Xyicon";
import {View} from "../models/View";
import type {FieldLayoutDefinitiontDto, UserDeletedDto} from "../../generated/api/base";
import {XyiconFeature} from "../../generated/api/base";
import type {Type} from "../models/Type";
import type {Field, IDefaultField, IFieldAdapter} from "../models/field/Field";
import type {ITypeFieldMapping} from "../models/TypeFieldMapping";
import type {Catalog} from "../models/Catalog";
import type {LayoutSettings} from "../../ui/modules/settings/modules/layout/LayoutSettings";
import type {App} from "../../App";
import type {Organization} from "../models/Organization";
import {Collection} from "../models/abstract/Collection";
import type {PortfolioGroup} from "../models/PortfolioGroup";
import type {UserGroup} from "../models/UserGroup";
import type {PermissionSet} from "../models/permission/PermissionSet";
import type {BoundarySpaceMap} from "../models/BoundarySpaceMap";
import type {DocumentModel} from "../models/DocumentModel";
import {configureUser} from "../models/field/datatypes/User";
import {configureType} from "../models/field/datatypes/Type";
import type {IPortfolioListScopeItem, Report} from "../models/Report";
import type {ThemeType} from "../../ui/ThemeType";
import {configureGeoLocation} from "../../ui/widgets/input/clicktoedit/datatypes/geolocation/GeoLocationLabel";
import type {Table} from "../../ui/widgets/table/Table";
import {FieldDataTypes} from "../models/field/FieldDataTypes";
import type {LayoutSettingsV5} from "../../ui/5.0/settings/modules/layout/LayoutSettingsV5";
import {NavigationEnum, PermissionSetSubNav, SettingsModuleNav, SettingsModuleSubNav, SettingsPermissionsSubNav, SettingsSubNav} from "../../Enums";
import type {Dashboard} from "../models/Dashboard";
import {StringUtils} from "../../utils/data/string/StringUtils";
import {configurePeopleSingle} from "../models/field/datatypes/PeopleSingle";
import {configurePeopleMultiple} from "../models/field/datatypes/PeopleMultiple";
import {notify} from "../../utils/Notify";
import {type INotificationParamsV5, NotificationType} from "../../ui/5.0/popup/NotificationV5";
import {AppFieldActions} from "./AppFields";
import {AppActions} from "./AppActions";
import {ItemFieldUpdateManager} from "./ItemFieldUpdateManager";
import type {FeatureMap} from "./AppStateConstants";
import type {FontObjectType} from "./AppStateTypes";

type StringEnumLike = Record<string, string>;

type IdMap<T> = {
	[id: string]: T;
};

export type UIVersion = "4.0" | "5.0";

export class AppState {
	public catalogEditorParentMaybe: HTMLDivElement = null;

	public user: User | null;

	public get organizationIdByAuthData(): string {
		return this._app?.transport?.services?.auth?.authData?.organizationID ?? "";
	}

	public get organizationIdByURL() {
		const cleanHash = StringUtils.cleanHash(location.hash);

		const parts = cleanHash.split("/");

		// Expected format: app/{guid}/...
		if (parts.length >= 2 && parts[0] === "app") {
			return parts[1];
		}

		return null;
	}

	public organizations: Organization[] = [];

	public readonly lists: {
		[key in XyiconFeature]?: Collection<any>;
	} = {
		[XyiconFeature.Portfolio]: new Collection<Portfolio>([], ["refId"]),
		[XyiconFeature.PortfolioGroup]: new Collection<PortfolioGroup>(),
		[XyiconFeature.XyiconCatalog]: new Collection<Catalog>(),
		[XyiconFeature.Dashboard]: new Collection<Dashboard>(),
		[XyiconFeature.LibraryImage]: new Collection<LibraryImage>(),
		[XyiconFeature.LibraryModel]: new Collection<LibraryModel>(),
		[XyiconFeature.User]: new Collection<User>(),
		[XyiconFeature.ExternalUser]: new Collection<ExternalUser>(),
		[XyiconFeature.UserGroup]: new Collection<UserGroup>(),
		[XyiconFeature.PermissionSet]: new Collection<PermissionSet>(),
		[XyiconFeature.OrganizationDocument]: new Collection<DocumentModel>(),
		// portfolio dependent lists:
		[XyiconFeature.Xyicon]: new Collection<Xyicon>(),
		[XyiconFeature.Space]: new Collection<Space>(),
		[XyiconFeature.SpaceVersion]: new Collection<SpaceVersion>(),
		[XyiconFeature.Markup]: new Collection<Markup>(),
		[XyiconFeature.Boundary]: new Collection<Boundary>(),
		[XyiconFeature.PortfolioDocument]: new Collection<DocumentModel>(),
		[XyiconFeature.Report]: new Collection<Report>(),
		[XyiconFeature.Link]: new Collection<Link>([], ["objectAID", "objectBID"]),
	};

	public readonly orgWideLists: {
		[key in XyiconFeature]?: Collection<any>;
	} = {
		// portfolio dependent lists:
		[XyiconFeature.Xyicon]: new Collection<Xyicon>(),
		[XyiconFeature.Space]: new Collection<Space>(),
		[XyiconFeature.SpaceVersion]: new Collection<SpaceVersion>(),
		[XyiconFeature.Markup]: new Collection<Markup>(),
		[XyiconFeature.Boundary]: new Collection<Boundary>(),
		[XyiconFeature.PortfolioDocument]: new Collection<DocumentModel>(),
		[XyiconFeature.Report]: new Collection<Report>(),
	};

	public boundarySpaceMaps = new Collection<BoundarySpaceMap>();

	public types: FeatureMap<Type[]> = {
		[XyiconFeature.Portfolio]: [],
		[XyiconFeature.Xyicon]: [],
		[XyiconFeature.Boundary]: [],
		[XyiconFeature.Space]: [],
	};

	public typesById: IdMap<Type> = {};
	public fieldsById: IdMap<Field> = {}; // only Field has id
	public fieldsByRef: IdMap<IFieldAdapter> = {}; // FieldAdapter has only refId
	public viewsMap: IdMap<View> = {};

	public fields: FeatureMap<Field[]> = {
		[XyiconFeature.Portfolio]: [],
		[XyiconFeature.XyiconCatalog]: [],
		[XyiconFeature.Xyicon]: [],
		[XyiconFeature.Space]: [],
		[XyiconFeature.Boundary]: [],
	};

	public typeFieldMapping: FeatureMap<ITypeFieldMapping> = {
		[XyiconFeature.Portfolio]: {},
		[XyiconFeature.Xyicon]: {},
		[XyiconFeature.XyiconCatalog]: {},
		[XyiconFeature.Space]: {},
		[XyiconFeature.Boundary]: {},
	};

	public views: FeatureMap<View[]> = {
		[XyiconFeature.Portfolio]: [],
		[XyiconFeature.Xyicon]: [],
		[XyiconFeature.Boundary]: [],
		[XyiconFeature.XyiconCatalog]: [],
		[XyiconFeature.Space]: [],
		[XyiconFeature.SpaceEditor]: [],
		[XyiconFeature.Report]: [],
	};

	public reportViews: Report[];

	// TODO should be less error prone -> auto create all default / inherited fields?
	public defaultViews: FeatureMap<View> = {
		[XyiconFeature.Portfolio]: View.createDefault(["refId", "name", "type"], XyiconFeature.Portfolio, this),
		[XyiconFeature.Xyicon]: View.createDefault(["refId", "type"], XyiconFeature.Xyicon, this),
		[XyiconFeature.Boundary]: View.createDefault(["refId", "name", "type"], XyiconFeature.Boundary, this),
		[XyiconFeature.XyiconCatalog]: View.createDefault(["refId", "type"], XyiconFeature.XyiconCatalog, this),
		[XyiconFeature.Space]: View.createDefault(["refId", "name", "type", "versionName"], XyiconFeature.Space, this),
		// [XyiconFeature.SpaceEditor]: View.createDefault(["refId", "name", "type"], XyiconFeature.SpaceEditor, this), // Add this separately, because it needs the appState to be fully initialized
		[XyiconFeature.Report]: View.createDefault(
			["refId", "name", "reportFeatureTitle", "scopeTitle", "category", "description", "lastModifiedBy", "lastModifiedAt"],
			XyiconFeature.Report,
			this,
			[90, 200, 80, 135, 100, 300, 136, 155],
		),
	};

	public touchedViews: string[] = [];

	public selectedViewId: FeatureMap<string> = {
		[XyiconFeature.Portfolio]: "",
		[XyiconFeature.Xyicon]: "",
		[XyiconFeature.Boundary]: "",
		[XyiconFeature.XyiconCatalog]: "",
		[XyiconFeature.Space]: "",
		[XyiconFeature.SpaceEditor]: "",
		[XyiconFeature.Report]: "",
	};

	public selectedReportViewId: string = "";

	public layouts: FeatureMap<FieldLayoutDefinitiontDto> = {};

	public readonly icons = {
		status: "",
		result: [] as Xyicon[],
	};

	public fonts: FontObjectType = {} as unknown as any;

	// TODO add # prefix for default fields?

	public readonly defaultFields: FeatureMap<IDefaultField[]> = {
		[XyiconFeature.Portfolio]: [],
		[XyiconFeature.XyiconCatalog]: [],
		[XyiconFeature.Space]: [],
		[XyiconFeature.SpaceEditor]: [],
		[XyiconFeature.Boundary]: [],
		[XyiconFeature.Xyicon]: [],
		[XyiconFeature.Report]: [],
	};

	public lastError = "";

	public theme: ThemeType = "light";

	public selectedFieldInputRefId: string = "";

	public isDetailsContainerOpened: boolean = false;

	public table = React.createRef<HTMLTableElement>();

	public tableComponent = React.createRef<Table<IModel>>(); // ref to the Table component (only in grid view, not in settings)

	public layoutSettings = React.createRef<LayoutSettings | LayoutSettingsV5>(); // ref to the Table component (only in grid view, not in settings)

	public isDetailsContentBeingOpenByDocumentOfItem: boolean = false; // if true -> the default state of ToggleContainers within the DetailsContent should be false, except for the Document section

	public isLoggingIn: boolean = false;

	public fullscreenLoaderText: string = "";
	private static instance: AppState;
	private readonly _actions: AppActions;
	private readonly _itemFieldUpdateManager: ItemFieldUpdateManager = new ItemFieldUpdateManager();

	private readonly _app: App;

	constructor(app: App) {
		makeObservable(this, {
			catalogEditorParentMaybe: observable,
			user: observable,
			organizationIdByAuthData: computed,
			organizations: observable,
			lists: observable,
			orgWideLists: observable,
			boundarySpaceMaps: observable,
			types: observable,
			fields: observable,
			typeFieldMapping: observable,
			views: observable,
			reportViews: observable,
			touchedViews: observable,
			selectedViewId: observable,
			selectedReportViewId: observable,
			layouts: observable,
			icons: observable,
			defaultFields: observable,
			lastError: observable,
			theme: observable,
			selectedFieldInputRefId: observable,
			isDetailsContainerOpened: observable,
			table: observable,
			tableComponent: observable,
			layoutSettings: observable,
			isDetailsContentBeingOpenByDocumentOfItem: observable,
			isLoggingIn: observable,
			fullscreenLoaderText: observable,
			itemFieldUpdateManager: computed,
			removedUsersList: observable,
			reportsUnderGenerating: observable,
		});
		this._app = app;
		if (AppState.instance) {
			console.warn("AppState already created!");
		}
		AppState.instance = this;

		this._actions = new AppActions(this);

		const fieldActions = new AppFieldActions(this);

		fieldActions.createDefaultFields();

		this.reconfigureFieldTypes();
	}

	private reconfigureFieldTypes() {
		FieldDataTypes.clear();

		// Needed for formatting in Table
		configureBoolean();
		configureDate();
		configureNumeric();
		configureSingleLine();
		configureMultiLine();
		configureSingleSelect();
		configureMultiSelect();
		configureEmail();
		configureIP();
		configurePhone();
		configurePeopleSingle();
		configurePeopleMultiple();
		configureGeoLocation();
		configureUser(this);
		configureType(this);
	}

	private pickFromHashEnum<E extends StringEnumLike>(hash: string, index: number, enumObj: E, fallback: E[keyof E]): E[keyof E] {
		const part = hash.split("/")[index];

		if (!part) {
			return fallback;
		}

		const values = Object.values(enumObj).toSorted((a, b) => b.length - a.length);

		return values.includes(part) ? (part as E[keyof E]) : fallback;
	}

	public get selectedSubMenu(): SettingsSubNav | null {
		if (this.selectedMenu !== NavigationEnum.SETTINGS) {
			return null;
		}

		return this.pickFromHashEnum(this._app.hash, 3, SettingsSubNav, null);
	}

	public get selectedPermissionMenu(): SettingsPermissionsSubNav | null {
		if (this.selectedSubMenu !== SettingsSubNav.PERMISSIONS) {
			return null;
		}

		return this.pickFromHashEnum(this._app.hash, 4, SettingsPermissionsSubNav, null);
	}

	public get selectedSettingsModuleMenu(): SettingsModuleNav | null {
		if (this.selectedMenu !== NavigationEnum.SETTINGS || this.selectedSubMenu !== SettingsSubNav.MODULES) {
			return null;
		}

		return this.pickFromHashEnum(this._app.hash, 4, SettingsModuleNav, null);
	}

	public get selectedSettingsModuleSubMenu(): SettingsModuleSubNav | null {
		if (!this.selectedSettingsModuleMenu) {
			return null;
		}

		return this.pickFromHashEnum(this._app.hash, 5, SettingsModuleSubNav, null);
	}

	public get selectedPermissionSetSubMenu(): PermissionSetSubNav | null {
		if (!this.selectedPermissionMenu) {
			return null;
		}

		return this.pickFromHashEnum(this._app.hash, 5, PermissionSetSubNav, null);
	}

	public get selectedMenu(): NavigationEnum {
		const {hash} = this._app;

		for (const patternObj of StringUtils.getHashBasePatterns(this.currentUIVersion)) {
			if (patternObj.pattern.test(hash)) {
				return patternObj.menu;
			}
		}

		return this.defaultMenu;
	}

	public get defaultMenu(): NavigationEnum {
		return this.currentUIVersion === "5.0" ? NavigationEnum.HOME : NavigationEnum.PORTFOLIOS;
	}

	public get selectedModuleTitle(): string {
		return this._app.navigation.getTitle(this.selectedMenu);
	}

	public get selectedFeature(): XyiconFeature {
		const menus = this.app.navigation.menus;
		const screens = this.app.navigation.screens;
		const currentMenu = this.selectedMenu;
		const selectedMenu = menus.find((m) => m.nav === currentMenu) ?? screens.find((s) => s.nav === currentMenu);

		return selectedMenu?.feature ?? null;
	}

	public initAdditionalDefaultViews() {
		if (!this.defaultViews[XyiconFeature.SpaceEditor]) {
			this.defaultViews[XyiconFeature.SpaceEditor] = View.createDefault(["refId", "name", "type"], XyiconFeature.SpaceEditor, this);
		}
	}

	public selectViewForFeatureAfterLogin(features: XyiconFeature[]) {
		if (this.organizationIdByAuthData) {
			for (const feature of features) {
				// If the local storage has a last used view for a given module use that
				const localStorageViewId =
					this._app.transport.services.localStorage.get(this.getKeyForLocalStorageView(this.organizationIdByAuthData, feature)) || "";

				if (localStorageViewId) {
					this.selectedViewId[feature] = localStorageViewId;
				} else {
					// Otherwise, if the local storage doesn't have a last used view for a given module,
					// load the default view defined under the default views for the same organization for the module.
					const organizationSettings = this.actions.getCurrentOrganizationSettings();
					const organizationViewId = organizationSettings?.defaultViews?.[feature];

					if (organizationViewId) {
						this.selectedViewId[feature] = organizationViewId;
					}
				}

				// Check if selected view exists
				if (!this._actions.getViewById(this.selectedViewId[feature])) {
					// selected view doesn't exist for some reason -> select the first view if there is at least one view
					const views = this.views[feature];
					const firstView = views?.length ? views[0] : null;

					if (firstView) {
						this.selectedViewId[feature] = firstView.id;
					} else {
						// No views for this feature, select the default view (which has id = "")
						this.selectedViewId[feature] = this.defaultViews[feature]?.id || "";
					}
				}
			}
		}
	}

	public getKeyForLocalStorageView(organizationId: string, feature: XyiconFeature) {
		return `srv4-org-${organizationId}-${this.user?.id}-view-${feature}`;
	}

	public getLocalStorageKeyForLastViewedSpace(userId: string, orgId: string) {
		return `last_viewed_space_${userId}_${orgId}_${this.activePortfolioId ?? this.lastUsedPortfolioId}`;
	}

	public async setUIVersion(version: UIVersion) {
		if (this.currentUIVersion !== version) {
			localStorage.setItem("ui-version", version);

			if (this.user?.generalSetting) {
				this.user.generalSetting.currentUiVersion = version;
			}

			await this._app.validateStateByURLHash();
			this._app.forceUpdate();

			// TODO: temp. to be removed along with version switcher
			// V5 greeting toast
			if (version === "5.0") {
				const notificationParams: INotificationParamsV5 = {
					type: NotificationType.Message,
					title: "Welcome to Version 5",
					description: "Learn more about what's new and how to use the latest features.",
					buttonLabel: "Learn more",
					onActionButtonClick: () => {
						this._app.navigation.openInNewTab("https://support.xyicon.com/docs/try-out-version-5");
					},
					lifeTime: Infinity,
				};
				notify(this._app.notificationContainer, notificationParams);
			}
		}
	}

	public get currentUIVersion(): UIVersion {
		let ret = (this.user?.generalSetting.currentUiVersion as UIVersion) || (localStorage.getItem("ui-version") as UIVersion);
		return ret === "5.0" ? "5.0" : "4.0";
	}

	public get actions() {
		return this._actions;
	}

	public get itemFieldUpdateManager() {
		return this._itemFieldUpdateManager;
	}

	public get organization(): Organization | undefined {
		return this.organizations.find((o) => o.id === this.organizationIdByAuthData);
	}

	public get app() {
		return this._app;
	}

	public get space() {
		const spaceRefIdMaybe = StringUtils.getSpaceRefIdFromHash(this._app.hash);

		if (spaceRefIdMaybe) {
			const spaceMaybe = this._actions.getList<Space>(XyiconFeature.Space).find((s) => s.refId === spaceRefIdMaybe);
			if (spaceMaybe && spaceMaybe.portfolioId === this.activePortfolioId) {
				return spaceMaybe;
			}
		}

		return null;
	}

	public get activePortfolioRefId() {
		return location.hash.match(/#app\/[^/]+(?:\/[^/]+)*\/(p\d+)/)?.[1] ?? null;
	}

	public get activePortfolio() {
		const portfolioRefId = this.activePortfolioRefId;

		if (portfolioRefId) {
			return this._actions.getPortfolioByRefId(portfolioRefId);
		} else if (this.currentUIVersion === "4.0") {
			const portfolioIdByAuthDataMaybe = this.lastUsedPortfolioId;
			if (portfolioIdByAuthDataMaybe) {
				return this._actions.getPortfolioById(portfolioIdByAuthDataMaybe);
			}
		}

		return null;
	}

	public get activePortfolioId() {
		return this.activePortfolio?.id;
	}

	public get lastUsedPortfolioId(): string {
		return this._app.transport.services.auth.authData?.portfolioID ?? "";
	}

	public get lastUsedPortfolio(): Portfolio | null {
		const portfolioId = this.lastUsedPortfolioId;

		if (portfolioId) {
			return this._actions.getPortfolioById(portfolioId);
		}

		return null;
	}

	public get relevantPortfolioId(): string {
		return this.activePortfolioId ?? this.lastUsedPortfolioId;
	}

	public get lastUsedPortfolioRefId(): string | null {
		return this.lastUsedPortfolio?.refId ?? null;
	}

	public removedUsersList: UserDeletedDto[] = [];

	public reportsUnderGenerating: string[] = [];

	public reportCachedPortfolioListScope: IPortfolioListScopeItem[] = []; // keeps a cache of portfolio names to show in the report Scope section
}
